import { extractFormValues } from "@components";
import { buildRequests } from "../../PaymentRequestDrawers/utils";

/**
 * Frontend channel policy: an attached PDF statement can't be delivered by SMS, so a statement
 * (`attachPdfStatement`) never uses the SMS channel — even when the customer has a mobile. The
 * backend stays the source of truth for contact details; this only drops SMS as a comm method.
 */
export const withoutSmsForStatements = (config) => {
  if (!config?.attachPdfStatement || !config.smsEnabled) return config;
  return { ...config, smsEnabled: false };
};

/**
 * Maps the flat `requestConfig` (the shape the intelligence-service returns and the Portal applies
 * verbatim) into the template shape the editor understands. Used to seed the preview and — via
 * `buildSuggestedRequestPayload` — to send the same request headlessly.
 *
 *   { name?, invoicesRequested?, customAmount?, mandatory?, attachPdfStatement?,
 *     paymentProfileId?, allowCardPayment?(legacy), emailEnabled?, smsEnabled?,
 *     email?: { subject?, title?, mainText?, actionTitle?, buttonText? }, sms?: { text? } }
 */
export const configToTemplate = (config) => {
  const emailOn = config.emailEnabled ?? !!config.email;
  const smsOn = config.smsEnabled ?? !!config.sms;

  const messages = [];
  if (emailOn) {
    messages.push({
      communicationMethod: "Email",
      content: {
        subject: config.email?.subject ?? "",
        title: config.email?.title ?? "",
        mainText: config.email?.mainText ?? "",
        actionArea: {
          title: config.email?.actionTitle ?? "",
          buttonText: config.email?.buttonText ?? "Review and pay",
        },
      },
    });
  }
  if (smsOn) {
    messages.push({
      communicationMethod: "Sms",
      content: { text: config.sms?.text ?? "" },
    });
  }

  return {
    name: config.name ?? "Custom request",
    invoicesRequested: config.invoicesRequested ?? "Custom",
    paymentRequirement: config.mandatory ? "Mandatory" : "Flexible",
    attachPdfStatement: config.attachPdfStatement ?? false,
    allowCardPayment: config.allowCardPayment ?? false,
    paymentProfileId: config.paymentProfileId ?? null,
    messages,
  };
};

/**
 * The inverse of `configToTemplate`: a saved payment-request template as the flat `requestConfig`, so a
 * merchant template can be previewed and sent headlessly through the same path as a suggestion.
 */
export const templateToRequestConfig = (template) => {
  const email = template.messages?.find((m) => m.communicationMethod === "Email")?.content;
  const sms = template.messages?.find((m) => m.communicationMethod === "Sms")?.content;
  return {
    name: template.name,
    invoicesRequested: template.invoicesRequested,
    mandatory: template.paymentRequirement === "Mandatory",
    attachPdfStatement: template.attachPdfStatement ?? false,
    allowCardPayment: template.allowCardPayment ?? false,
    paymentProfileId: template.paymentProfileId ?? null,
    emailEnabled: !!email,
    smsEnabled: !!sms,
    email: email
      ? {
          subject: email.subject,
          title: email.title,
          mainText: email.mainText,
          actionTitle: email.actionArea?.title,
          buttonText: email.actionArea?.buttonText,
        }
      : undefined,
    sms: sms ? { text: sms.text } : undefined,
  };
};

/**
 * Builds the batch `meta` from resolved form values. Shared by the modal's confirm step and the
 * headless suggested-send so both emit an identical payload for the same inputs.
 */
export const buildBatchMeta = ({
  values,
  invoicesRequested,
  templateName = null,
  templateId,
}) => {
  const isCustom = invoicesRequested === "Custom";
  return {
    batchName: templateName ?? "Template batch",
    invoicesRequested: isCustom ? null : invoicesRequested,
    amountRequested: isCustom ? values.customAmount : undefined,
    allowAdditionalInvoices: isCustom ? null : true,
    invoicesRequestedAreMandatory: isCustom
      ? null
      : (values.mandatory ?? false),
    includePdfStatement: isCustom ? null : (values.attachPdfStatement ?? false),
    subject: values.emailSubject ?? "",
    smsMessage: values.smsText ?? "",
    emailBody: values.emailMainText ?? "",
    allowCardPayment: values.allowCardPayment ?? false,
    paymentProfileId: values.paymentProfileId ?? null,
    templateId,
    sendAsPlainText: false,
    messageTemplateName: templateName ?? null,
    emailContent: {
      format: "StructuredV1",
      title: values.emailTitle,
      mainText: values.emailMainText,
      actionArea: {
        title: values.emailActionTitle,
        description: "",
        amount: values.amountDisplay,
        buttonText: values.emailButtonText,
      },
    },
  };
};

/**
 * Headless equivalent of seeding the modal from a `requestConfig` and confirming with no edits:
 * resolves the same form values, channels, rows and meta the preview would produce, ready to hand
 * to `sendBatch`. `customer` must be a full customer record (contact + aged-debt fields), or a list
 * of them for one batch.
 */
export const buildSuggestedRequestPayload = (customer, requestConfig) => {
  const template = configToTemplate(requestConfig ?? {});
  const values = {
    ...extractFormValues(template),
    customAmount: requestConfig?.customAmount,
  };
  const emailEnabled = template.messages.some(
    (m) => m.communicationMethod === "Email",
  );
  const smsEnabled = template.messages.some(
    (m) => m.communicationMethod === "Sms",
  );
  const invoicesRequested = values.invoicesRequested ?? "Custom";

  return {
    rows: buildRequests(Array.isArray(customer) ? customer : [customer], invoicesRequested),
    strategy: { sms: smsEnabled, email: emailEnabled },
    meta: buildBatchMeta({
      values,
      invoicesRequested,
      templateName: template.name,
    }),
  };
};
